import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { invoicesAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { Mail, Download } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCompany } from '../common/useCompany';
import InvoiceDocument from '../common/InvoiceDocument';
import EmailModal from '../common/EmailModal';
import PrintButton from '../../utils/print';
import { generateInvoicePDF } from '../../utils/generateDocumentPDF';
import { fmtMoney } from '../../utils/currency';

const fmt = fmtMoney;

export default function InvoiceView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const company = useCompany();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEmail, setShowEmail] = useState(false);
  const docRef = useRef(null);

  const load = () => invoicesAPI.get(id).then(setInvoice).catch(e => toast.error(e.message)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  const handleSend = async () => { try { await invoicesAPI.send(id); toast.success('Invoice sent & posted!'); load(); } catch(e) { toast.error(e.message); } };
  const handleVoid = async () => { const reason = prompt('Reason:'); if(!reason)return; try { await invoicesAPI.void(id, {reason}); toast.success('Invoice voided'); load(); } catch(e) { toast.error(e.message); } };
  const handleDelete = async () => {
    const msg = invoice?.status === 'Voided'
      ? `Permanently delete voided invoice ${invoice.invoice_number}? This removes it and its journal entries with no trace.`
      : `Delete invoice ${invoice?.invoice_number}?`;
    if (!window.confirm(msg)) return;
    try { await invoicesAPI.remove(id); toast.success('Invoice deleted'); navigate('/invoices'); } catch(e) { toast.error(e.message); }
  };

  if (loading) return <div className="spinner" style={{margin:'40px auto'}} />;
  if (!invoice) return <div className="empty-state"><h3>Invoice not found</h3></div>;

  const docData = {
    invoice_number: invoice.invoice_number,
    invoice_date: invoice.invoice_date,
    due_date: invoice.due_date,
    payment_terms: invoice.payment_terms,
    status: invoice.status,
    currency: invoice.currency,
    exchange_rate: invoice.exchange_rate,
    customer_name: invoice.customer_name,
    customer_email: invoice.customer_email,
    customer_phone: invoice.customer_phone,
    customer_address: [invoice.cust_addr1, [invoice.cust_city, invoice.cust_state, invoice.cust_zip].filter(Boolean).join(', ')].filter(Boolean).join(', '),
    items: invoice.items,
    subtotal: invoice.subtotal,
    tax_amount: invoice.tax_amount,
    total: invoice.total,
    amount_paid: invoice.amount_paid,
    amount_due: invoice.amount_due,
    memo: invoice.memo || invoice.notes,
  };

  return (
    <div>
      <div className="toolbar">
        <h2>Invoice {invoice.invoice_number}</h2>
        <div className="toolbar-right">
          <span className={`badge badge-${invoice.status?.toLowerCase()}`} style={{fontSize:13,padding:'6px 14px'}}>{invoice.status}</span>
          <PrintButton targetRef={docRef} title={`Invoice ${invoice.invoice_number}`} />
          <button className="btn btn-outline" onClick={async () => { try { await generateInvoicePDF(docData, company); } catch(e) { toast.error('PDF generation failed: ' + e.message); } }} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Download size={14} /> PDF
          </button>
          <button className="btn btn-outline" onClick={() => setShowEmail(true)} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Mail size={14} /> Email
          </button>
          {invoice.status === 'Draft' && hasPermission('invoices:post') && <button className="btn btn-success" onClick={handleSend}>Send Invoice</button>}
          {invoice.status === 'Draft' && hasPermission('invoices:edit') && <button className="btn btn-outline" onClick={() => navigate(`/invoices/${id}/edit`)}>Edit</button>}
          {['Sent','Viewed','Partial'].includes(invoice.status) && hasPermission('invoices:void') && <button className="btn btn-danger" onClick={handleVoid}>Void</button>}
          {(invoice.status === 'Draft' || invoice.status === 'Voided') && hasPermission('invoices:edit') && <button className="btn btn-danger" onClick={handleDelete}>Delete</button>}
          <button className="btn btn-outline" onClick={() => navigate('/invoices')}>Back</button>
        </div>
      </div>

      <div className="inv-doc-shell" ref={docRef}>
        <InvoiceDocument data={docData} docType="invoice" />
      </div>

      {(invoice.payments || []).length > 0 && (
        <div className="card" style={{ marginTop: 16, maxWidth: 820, marginInline: 'auto' }}>
          <div className="card-header"><h3>Payments</h3></div>
          <div className="card-body" style={{padding:0}}>
            <table>
              <thead><tr><th>Date</th><th>Method</th><th>Reference</th><th className="text-right">Amount</th></tr></thead>
              <tbody>{invoice.payments.map((p,i) => <tr key={i}><td>{p.payment_date}</td><td>{p.payment_method}</td><td>{p.reference || '-'}</td><td className="amount">{fmt(p.amount)}</td></tr>)}</tbody>
            </table>
          </div>
        </div>
      )}
      <EmailModal
        show={showEmail}
        onClose={() => setShowEmail(false)}
        type="invoice"
        refId={invoice.id}
        number={invoice.invoice_number}
        recipientEmail={invoice.customer_email || ''}
        recipientName={invoice.customer_name || ''}
      />
    </div>
  );
}
